import React from 'react';
import { theme } from '../styles/design-system';
import {
    Database,
    Brain,
    Zap,
    Activity,
    ArrowRight,
    X,
    Globe,
    Cpu,
    TrendingUp
} from 'lucide-react';

const WorkflowExplanation = ({ isOpen, onClose, type = 'algo' }) => {
    if (!isOpen) return null;

    const steps = type === 'algo' ? [
        {
            icon: <Globe size={24} color="#3B82F6" />,
            title: "1. Market Data Ingestion",
            description: "We stream real-time tick data (price, volume) from Zerodha Kite Connect API and historical data from Yahoo Finance.",
            details: ["Tick-by-tick updates", "Historical candles (1m, 5m, 15m)", "Volume analysis"]
        },
        {
            icon: <Cpu size={24} color="#8B5CF6" />,
            title: "2. Multi-Factor Analysis",
            description: "Our engine processes data through three distinct layers:",
            details: [
                "Technical: RSI, MACD, Moving Averages",
                "Astrological: Planetary positions & Nakshatras",
                "Numerological: Date/Time compatibility"
            ]
        },
        {
            icon: <Brain size={24} color="#EC4899" />,
            title: "3. AI Decision Engine",
            description: "The core logic combines all signals to calculate a 'Confidence Score'.",
            details: ["Weighted scoring system", "Risk/Reward calculation", "Trend confirmation"]
        },
        {
            icon: <Zap size={24} color="#F59E0B" />,
            title: "4. Execution & Management",
            description: "If the score exceeds the threshold, an order is placed automatically.",
            details: ["Smart order placement", "Stop-loss & Target tracking", "Auto-trailing"]
        }
    ] : [
        // F&O Specific Workflow
        {
            icon: <Activity size={24} color="#3B82F6" />,
            title: "1. Option Chain Scanning",
            description: "We scan the entire Option Chain for NIFTY/BANKNIFTY in real-time.",
            details: ["Open Interest (OI) analysis", "PCR (Put-Call Ratio) calculation", "IV (Implied Volatility) monitoring"]
        },
        {
            icon: <Cpu size={24} color="#8B5CF6" />,
            title: "2. Astro-Technical Filter",
            description: "We filter strikes based on astrological timing (Muhurta) and technical levels.",
            details: ["Gann Square levels", "Planetary hours", "Support/Resistance zones"]
        },
        {
            icon: <TrendingUp size={24} color="#10B981" />,
            title: "3. Strategy Selection",
            description: "The system selects the best strategy (Long Call/Put, Spreads) based on market direction.",
            details: ["Directional bias detection", "Volatility adjustment", "Premium decay analysis"]
        },
        {
            icon: <Zap size={24} color="#F59E0B" />,
            title: "4. Trade Lifecycle",
            description: "Automated entry and exit based on premium targets.",
            details: ["Quick scalping logic", "Trailing stop-loss", "Profit booking at levels"]
        }
    ];

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={styles.title}>
                        {type === 'algo' ? 'How Our Algo Trading Works' : 'F&O Trading Workflow'}
                    </h2>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X size={24} />
                    </button>
                </div>

                <div style={styles.content}>
                    <p style={styles.intro}>
                        Our platform combines traditional technical analysis with advanced astrological insights to give you a unique edge. Here's what happens under the hood:
                    </p>

                    <div style={styles.timeline}>
                        {steps.map((step, index) => (
                            <div key={index} style={styles.step}>
                                <div style={styles.iconContainer}>
                                    {step.icon}
                                    {index < steps.length - 1 && <div style={styles.connector} />}
                                </div>
                                <div style={styles.stepContent}>
                                    <h3 style={styles.stepTitle}>{step.title}</h3>
                                    <p style={styles.stepDesc}>{step.description}</p>
                                    <ul style={styles.detailsList}>
                                        {step.details.map((detail, i) => (
                                            <li key={i} style={styles.detailItem}>• {detail}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(5px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
    },
    modal: {
        backgroundColor: theme.colors.background.surface.dark,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: `1px solid ${theme.colors.gray[700]}`,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
    },
    header: {
        padding: '24px',
        borderBottom: `1px solid ${theme.colors.gray[700]}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        backgroundColor: theme.colors.background.surface.dark,
        zIndex: 10
    },
    title: {
        fontSize: '20px',
        fontWeight: '600',
        color: theme.colors.gray[50],
        margin: 0
    },
    closeButton: {
        background: 'none',
        border: 'none',
        color: theme.colors.gray[400],
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s'
    },
    content: {
        padding: '24px'
    },
    intro: {
        color: theme.colors.gray[400],
        marginBottom: '32px',
        lineHeight: '1.6'
    },
    timeline: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0px'
    },
    step: {
        display: 'flex',
        gap: '20px',
        position: 'relative',
        paddingBottom: '32px'
    },
    iconContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '40px'
    },
    connector: {
        width: '2px',
        flex: 1,
        backgroundColor: theme.colors.gray[700],
        marginTop: '10px',
        marginBottom: '-10px'
    },
    stepContent: {
        flex: 1
    },
    stepTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: theme.colors.gray[50],
        marginBottom: '8px',
        marginTop: '2px'
    },
    stepDesc: {
        fontSize: '14px',
        color: theme.colors.gray[400],
        marginBottom: '12px',
        lineHeight: '1.5'
    },
    detailsList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
    },
    detailItem: {
        fontSize: '12px',
        color: theme.colors.primary[400],
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        padding: '4px 8px',
        borderRadius: '4px'
    }
};

export default WorkflowExplanation;
